import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link as MuiLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  fetchAttendeeDetail,
  clearAttendeeDetail,
} from "../../../../redux/features/attendeesSlice";
import moment from "moment";

function formatCents(cents) {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((cents || 0) / 100);
}

function parseDate(value) {
  if (value == null) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number")
    return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  return null;
}

function formatDate(value) {
  const d = parseDate(value);
  return d ? moment(d).format("MMM D, YYYY") : "—";
}

function getTicketTypeName(eventsMap, eventId, ticketTypeKey) {
  const event = eventsMap?.[eventId];
  if (!event?.ticketTypes?.length || !ticketTypeKey) return ticketTypeKey || "—";
  const tt = event.ticketTypes.find((t) => t.key === ticketTypeKey);
  return tt?.name ?? ticketTypeKey;
}

function formatLocation(attendee) {
  const loc = attendee?.locationFromZipcode;
  const zip = attendee?.zipcode;
  if (loc?.city && loc?.state) {
    return zip ? `${zip} (${loc.city}, ${loc.state})` : `${loc.city}, ${loc.state}`;
  }
  return zip || "—";
}

/** If values are all the same, return single display value; else return { mixed: true, tooltip: string }. */
function summarizeValues(values, formatter = (v) => String(v)) {
  const uniq = [...new Set(values.map(formatter))];
  if (uniq.length <= 1) return { mixed: false, value: uniq[0] ?? "—" };
  return { mixed: true, tooltip: values.map(formatter).join("\n") };
}

function MixedOrValue({ summary }) {
  if (!summary.mixed) return summary.value;
  return (
    <Tooltip
      title={
        <span style={{ whiteSpace: "pre-line" }}>{summary.tooltip}</span>
      }
    >
      <span style={{ textDecoration: "underline dotted", cursor: "help" }}>
        mixed
      </span>
    </Tooltip>
  );
}

export default function AttendeeDetail() {
  const { organizationId, attendeeId } = useParams();
  const dispatch = useDispatch();
  const key = `${organizationId}/${attendeeId}`;
  const detail = useSelector((state) => state.attendees?.detail?.[key]);
  const detailLoading = useSelector(
    (state) => state.attendees?.detailLoading?.[key],
  );

  useEffect(() => {
    if (organizationId && attendeeId) {
      dispatch(fetchAttendeeDetail({ organizationId, attendeeId }));
    }
    return () => {
      if (organizationId && attendeeId) {
        dispatch(clearAttendeeDetail({ organizationId, attendeeId }));
      }
    };
  }, [dispatch, organizationId, attendeeId]);

  const ticketList = useMemo(
    () => detail?.tickets ?? [],
    [detail?.tickets],
  );
  const eventsMap = useMemo(
    () => detail?.eventsMap ?? {},
    [detail?.eventsMap],
  );
  const [groupBy, setGroupBy] = useState("none");

  const groupedRows = useMemo(() => {
    if (groupBy === "none" || ticketList.length === 0) return null;
    const key = groupBy === "event" ? "eventId" : "orderId";
    const groups = new Map();
    for (const t of ticketList) {
      const k = t[key] ?? "";
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(t);
    }
    return Array.from(groups.entries()).map(([groupKey, groupTickets]) => {
      const eventIds = [...new Set(groupTickets.map((t) => t.eventId).filter(Boolean))];
      const orderIds = [...new Set(groupTickets.map((t) => t.orderId).filter(Boolean))];
      const types = groupTickets.map((t) =>
        getTicketTypeName(eventsMap, t.eventId, t.ticketTypeKey),
      );
      const purchaserSummary = summarizeValues(
        groupTickets.map((t) => t.isPurchaser),
        (v) => (v ? "Yes" : "No"),
      );
      const checkedInValues = groupTickets.map((t) =>
        !!(t.checkIn || (t.sessionCheckIns && Object.keys(t.sessionCheckIns || {}).length > 0)),
      );
      const checkedInSummary = summarizeValues(checkedInValues, (v) => (v ? "Yes" : "No"));
      const typeSummary = summarizeValues(types, (v) => v);
      const dates = groupTickets.map((t) => formatDate(t.createdAt));
      const dateSummary = summarizeValues(dates, (v) => v);
      const firstTicket = groupTickets[0];
      return {
        key: groupKey || `empty-${groupKey}`,
        groupTickets,
        eventIds,
        orderIds,
        firstTicket,
        purchaserSummary,
        checkedInSummary,
        typeSummary,
        dateSummary,
      };
    });
  }, [groupBy, ticketList, eventsMap]);

  if (detailLoading && !detail) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail?.attendee) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Attendee not found.
      </Alert>
    );
  }

  const { attendee } = detail;

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        component={Link}
        to={`/organizations/${organizationId}/attendees`}
        sx={{ mb: 2 }}
      >
        Back to attendees
      </Button>

      <Typography variant="h5" sx={{ mb: 1 }}>
        {attendee.displayName || "—"}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {attendee.email}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {attendee?.locationFromZipcode?.city && attendee?.locationFromZipcode?.state
            ? formatLocation(attendee)
            : `Zipcode: ${attendee?.zipcode || "—"}`
          }
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="body2">
            Tickets (as purchaser): {attendee.ticketCountAsPurchaser ?? 0}
          </Typography>
          <Typography variant="body2">
            Tickets (as attendee only): {attendee.ticketCountAsAttendeeOnly ?? 0}
          </Typography>
          <Typography variant="body2">
            Orders: {(attendee.orderCountAsPurchaser ?? 0) + (attendee.orderCountAsAttendeeOnly ?? 0)}
          </Typography>
          <Typography variant="body2">
            Events attended: {attendee.eventCountTotal ?? 0}
          </Typography>
          <Typography variant="body2">
            Total spend: {formatCents(attendee.totalSpendUsdCents)}
          </Typography>
          <Typography variant="body2">
            Checked in: {attendee.checkedInCountTotal ?? 0}
          </Typography>
          <Typography variant="body2">
            Last seen:{" "}
            {(() => {
              const d = parseDate(attendee.lastSeenAt);
              return d ? moment(d).format("MMM D, YYYY h:mm A") : "—";
            })()}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle1">Ticket history</Typography>
        <FormControl size="small" sx={{ minWidth: 160, ml: "auto" }}>
          <InputLabel id="ticket-history-group-by-label">Group by</InputLabel>
          <Select
            labelId="ticket-history-group-by-label"
            id="ticket-history-group-by"
            value={groupBy}
            label="Group by"
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="event">Event</MenuItem>
            <MenuItem value="order">Order</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" aria-label="Ticket history">
          <TableHead>
            <TableRow>
              <TableCell>Event</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Purchaser?</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Checked in</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedRows
              ? groupedRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>
                      {row.eventIds.length === 1 ? (
                        row.firstTicket.eventId ? (
                          <MuiLink
                            component={Link}
                            to={`/events/${row.firstTicket.eventId}`}
                            underline="hover"
                          >
                            {eventsMap[row.firstTicket.eventId]?.name ?? row.firstTicket.eventId}
                          </MuiLink>
                        ) : (
                          "—"
                        )
                      ) : (
                        <MixedOrValue
                          summary={{
                            mixed: true,
                            tooltip: row.eventIds
                              .map(
                                (eid) =>
                                  eventsMap[eid]?.name ?? eid,
                              )
                              .join("\n"),
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.orderIds.length === 1 ? (
                        row.firstTicket.orderId ? (
                          <MuiLink
                            component={Link}
                            to={`/orders/${row.firstTicket.orderId}`}
                            underline="hover"
                          >
                            {row.firstTicket.orderId}
                          </MuiLink>
                        ) : (
                          "—"
                        )
                      ) : (
                        <MixedOrValue
                          summary={{
                            mixed: true,
                            tooltip: row.orderIds.join("\n"),
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <MixedOrValue summary={row.typeSummary} />
                    </TableCell>
                    <TableCell>
                      <MixedOrValue summary={row.purchaserSummary} />
                    </TableCell>
                    <TableCell>
                      <MixedOrValue summary={row.dateSummary} />
                    </TableCell>
                    <TableCell>
                      <MixedOrValue summary={row.checkedInSummary} />
                    </TableCell>
                  </TableRow>
                ))
              : ticketList.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      {t.eventId ? (
                        <MuiLink
                          component={Link}
                          to={`/events/${t.eventId}`}
                          underline="hover"
                        >
                          {eventsMap[t.eventId]?.name ?? t.eventId}
                        </MuiLink>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {t.orderId ? (
                        <MuiLink
                          component={Link}
                          to={`/orders/${t.orderId}`}
                          underline="hover"
                        >
                          {t.orderId}
                        </MuiLink>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {getTicketTypeName(eventsMap, t.eventId, t.ticketTypeKey)}
                    </TableCell>
                    <TableCell>{t.isPurchaser ? "Yes" : "No"}</TableCell>
                    <TableCell>{formatDate(t.createdAt)}</TableCell>
                    <TableCell>
                      {t.checkIn ||
                      (t.sessionCheckIns &&
                        Object.keys(t.sessionCheckIns || {}).length > 0)
                        ? "Yes"
                        : "No"}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
